/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {PageTitle} from '../../../_metronic/layout/core'
import {CardsWidget17, CardsWidget20, EngageWidget10} from '../../../_metronic/partials/widgets'
import {useQuery} from '@apollo/client'
import {GET_MEDIA_COUNT} from '../../queries/media.query'
import {CardsWidget18} from '../../../_metronic/partials/widgets/_new/cards/CardWidget18'
import {GET_ALBUMS_COUNT} from '../../queries/album.query'
import {GET_MONTHLY_USER_ANALYTICS} from '../../queries/user.query'
import usePriority from '../../hooks/usePriority'
import useUsers from '../../hooks/useUsers'
import {DashboardFilter} from './DashboardFilter'
import {DataExchange} from 'aws-sdk'
import {MediaMetricsDashboard} from '../../components/media-metrics'

// Skeleton Loading Component for Cards
const CardSkeleton: FC<{className?: string}> = ({className = ''}) => (
  <div className={`card ${className}`}>
    <div className='card-body d-flex flex-column justify-content-between' style={{minHeight: '100px'}}>
      <div className='d-flex flex-column'>
        <div className='placeholder-glow mb-3'>
          <span className='placeholder col-8' style={{height: '24px', borderRadius: '4px'}}></span>
        </div>
        <div className='placeholder-glow'>
          <span className='placeholder col-5' style={{height: '14px', borderRadius: '4px'}}></span>
        </div>
      </div>
      <div className='placeholder-glow mt-auto'>
        <span className='placeholder col-12' style={{height: '6px', borderRadius: '3px'}}></span>
      </div>
    </div>
  </div>
)

// Helper component to conditionally render card or skeleton
const ConditionalCard: FC<{
  isLoading: boolean
  className?: string
  cardNumber: string
  description: string
  progressNumber?: string
  progressBar?: string
  color?: string
  img?: string
}> = ({ isLoading, className = '', cardNumber, description, progressNumber = '', progressBar = '', color = '#7239EA', img = '' }) => (
  <>
    {isLoading ? (
      <CardSkeleton className={className} />
    ) : (
      <CardsWidget20
        className={className}
        cardNumber={cardNumber}
        description={description}
        progressNumber={progressNumber}
        progressBar={progressBar}
        color={color}
        img={img}
      />
    )}
  </>
)

type MediaData = {
  videoCount: string
  audioCount: string
  reelsCount: string
}

type AlbumData = {
  audioAlbum: string
  videoAlbum: string
}

type chartData = {
  header: string
  total: string
  maleCount: string
  femaleCount: string
  increasePercentage: string
  thisMonthFreeUsers?: string
  iosUsersCount?: string
  androidUsersCount?: string
  freeUsersCount?: string
  subscribedUsersCount?: string
  totalAndroidMonthlySubscribedUsers?: string
  totalIosMonthlySubscribedUsers?: string
  totalAndroidSubscribedUsers?: string
  totalAndroidYearlySubscribedUsers?: string
  totalIosSubscribedUsers?: string
  totalIosYearlySubscribedUsers?: string
  totalNewSubscribers?: string
  renewedCount?: string
  totalAudioSubscribers?: string
  totalAudioMonthlySubscribers?: string
  totalAudioYearlySubscribers?: string
  totalAndroidAudioMonthlySubscribers?: string
  totalAndroidAudioYearlySubscribers?: string
  totalIosAudioMonthlySubscribers?: string
  totalIosAudioYearlySubscribers?: string
}

type Platform = {
  totalAndroidCount: string
  totalIosCount: string
  totalAndroidCountM: string
  totalAndroidCountY: string
  totalIosCountM: string
  totalIosCountY: string
  totalAndroidMonthlyPlan: string
  totalIosMonthlyPlan: string
  totalAndroidYearlyPlan: string
  totalIosYearlyPlan: string
  totalActiveUsers: string
  activeAndroidUsersCount: string
  activeIosUsersCount: string
  allAndroidCount: string
  allIosCount: string
  allAndroidMonthlyPlan: string
  allIosMonthlyPlan: string
  allAndroidYearlyPlan: string
  allIosYearlyPlan: string
}
const DashboardPage: FC = () => {
  const mediaResponse = useQuery(GET_MEDIA_COUNT)
  const albumResponse = useQuery(GET_ALBUMS_COUNT)
  const monthlyAnalyticsResponse = useQuery(GET_MONTHLY_USER_ANALYTICS)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [filterMode, setFilterMode] = useState<'overall' | 'current_month' | 'custom'>('overall')
  
  // Global loading state
  const [isDataLoading, setIsDataLoading] = useState(true)

  const {getPrioritiesAnalytics} = usePriority()

  const {
    getOverallUserAnalytics,
    getOverAllSubscriptionsAnalytics,
    getMonthlySubscriptionsAnalytics,
    getPlatformAnalytics,
  } = useUsers()

  // Helper function to safely parse integers with default value
  const safeParseInt = (value: any, defaultValue: number = 0): number => {
    const parsed = parseInt(value)
    return isNaN(parsed) ? defaultValue : parsed
  }

  const [platformAnalytics, setPlatformAnalytics] = useState<Platform>({
    totalAndroidCount: '0',
    totalIosCount: '0',
    totalAndroidCountM: '0',
    totalAndroidCountY: '0',
    totalIosCountM: '0',
    totalIosCountY: '0',
    totalAndroidMonthlyPlan: '0',
    totalIosMonthlyPlan: '0',
    totalAndroidYearlyPlan: '0',
    totalIosYearlyPlan: '0',
    totalActiveUsers: '0',
    activeAndroidUsersCount: '0',
    activeIosUsersCount: '0',
    allAndroidCount: '0',
    allIosCount: '0',
    allAndroidMonthlyPlan: '0',
    allIosMonthlyPlan: '0',
    allAndroidYearlyPlan: '0',
    allIosYearlyPlan: '0',
  })

  const [monthlyRevenues, setMonthlyRevenues] = useState(0)
  const [monthlySubscription, setMonthlySubscription] = useState<any>({
    header: 'Monthly Subscriptions',
    freeUsers: '0',
    subscribedUsers: '0',
  })

  const [overAllSubscription, setOverAllSubscription] = useState<any>({
    freeUsers: '0',
    subscribedUsers: '0',
    header: 'Overall Subscriptions',
  })

  const [mediaData, setMediaData] = useState<MediaData>({
    videoCount: '0',
    audioCount: '0',
    reelsCount: '0',
  })
  
  const [albumCount, setAlbumCount] = useState<AlbumData>({
    audioAlbum: '0',
    videoAlbum: '0',
  })
  
  const [chartData, setChartData] = useState<chartData>({
    header: 'Registration By Month',
    total: '0',
    maleCount: '0',
    femaleCount: '0',
    increasePercentage: '0',
    thisMonthFreeUsers: '0',
    iosUsersCount: '0',
    androidUsersCount: '0',
    freeUsersCount: '0',
    subscribedUsersCount: '0',
    totalAndroidMonthlySubscribedUsers: '0',
    totalIosMonthlySubscribedUsers: '0',
    totalAndroidSubscribedUsers: '0',
    totalAndroidYearlySubscribedUsers: '0',
    totalIosSubscribedUsers: '0',
    totalIosYearlySubscribedUsers: '0',
    totalNewSubscribers: '0',
    renewedCount: '0',
    totalAudioSubscribers: '0',
    totalAudioMonthlySubscribers: '0',
    totalAudioYearlySubscribers: '0',
    totalAndroidAudioMonthlySubscribers: '0',
    totalAndroidAudioYearlySubscribers: '0',
    totalIosAudioMonthlySubscribers: '0',
    totalIosAudioYearlySubscribers: '0',
  })

  const [prioritiesData, setPrioritiesData] = useState<any>({
    featuredSeriesCount: '0',
    featuredVideosCount: '0',
    popularSeriesCount: '0',
    recommendedPlaylistsCount: '0',
  })

  const [overallData, setOverAllData] = useState<chartData>({
    header: ' Overall Registration',
    total: '0',
    maleCount: '0',
    femaleCount: '0',
    increasePercentage: '0',
    iosUsersCount: '0',
    androidUsersCount: '0',
    freeUsersCount: '0',
    subscribedUsersCount: '0',
    totalAndroidMonthlySubscribedUsers: '0',
    totalIosMonthlySubscribedUsers: '0',
    totalAndroidSubscribedUsers: '0',
    totalAndroidYearlySubscribedUsers: '0',
    totalIosSubscribedUsers: '0',
    totalIosYearlySubscribedUsers: '0',
    totalNewSubscribers: '0',
    renewedCount: '0',
    totalAudioSubscribers: '0',
    totalAudioMonthlySubscribers: '0',
    totalAudioYearlySubscribers: '0',
    totalAndroidAudioMonthlySubscribers: '0',
    totalAndroidAudioYearlySubscribers: '0',
    totalIosAudioMonthlySubscribers: '0',
    totalIosAudioYearlySubscribers: '0',
  })

  // Initial data fetch - Load all static data in parallel
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsDataLoading(true)
      try {
        // Fetch all initial data in parallel
        const [platformRes, prioritiesRes, monthlySubsRes] = await Promise.all([
          getPlatformAnalytics(''),
          getPrioritiesAnalytics(),
          getMonthlySubscriptionsAnalytics(),
        ])

        // Update platform analytics
        if (platformRes?.data?.getPlatformBasedSubscriptions) {
          setPlatformAnalytics(platformRes.data.getPlatformBasedSubscriptions)
          
          const iosCount = safeParseInt(platformRes.data.getPlatformBasedSubscriptions?.totalIosCountM)
          const androidCount = safeParseInt(platformRes.data.getPlatformBasedSubscriptions?.totalAndroidCountM)
          const monthlyRevenue = (iosCount + androidCount) * 9.99
          setMonthlyRevenues(monthlyRevenue)
        }

        // Update priorities data
        if (prioritiesRes?.data?.prioritiesAnalytics) {
          setPrioritiesData(prioritiesRes.data.prioritiesAnalytics)
        }

        // Update monthly subscriptions
        if (monthlySubsRes?.data?.monthlySubscriptionAnalytics) {
          setMonthlySubscription({
            ...monthlySubsRes.data.monthlySubscriptionAnalytics,
            header: 'Monthly Subscription Analytics',
          })
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
      } finally {
        setIsDataLoading(false)
      }
    }

    fetchInitialData()
  }, [])

  // Handle GraphQL query results
  useEffect(() => {
    if (!mediaResponse.loading && mediaResponse.data?.getMediaCount?.data) {
      setMediaData(mediaResponse.data.getMediaCount.data)
    }
  }, [mediaResponse.loading, mediaResponse.data])

  useEffect(() => {
    if (!albumResponse.loading && albumResponse.data?.getAlbumCountsR?.data) {
      setAlbumCount(albumResponse.data.getAlbumCountsR.data)
    }
  }, [albumResponse.loading, albumResponse.data])

  useEffect(() => {
    if (!monthlyAnalyticsResponse.loading && monthlyAnalyticsResponse.data?.getMonthlyUserStatsR?.data) {
      const counts = monthlyAnalyticsResponse.data.getMonthlyUserStatsR.data
      setChartData({...counts, header: 'Registration By Month'})
    }
  }, [monthlyAnalyticsResponse.loading, monthlyAnalyticsResponse.data])

  // Fetch filtered data when filter changes
  useEffect(() => {
    const fetchFilteredData = async () => {
      setIsDataLoading(true)
      try {
        const dates = filterMode === 'custom' ? [startDate, endDate] : ['', '']
        
        // Fetch both analytics in parallel
        const [overallRes, subsRes] = await Promise.all([
          getOverallUserAnalytics(dates[0], dates[1]),
          getOverAllSubscriptionsAnalytics(dates[0], dates[1]),
        ])

        if (overallRes?.data?.overAllRegisteredAnalytics) {
        setOverAllData({
            ...overallRes.data.overAllRegisteredAnalytics,
          increasePercentage: chartData.increasePercentage,
          header: 'Overall Registration',
        })
        }

        if (subsRes?.data?.overAllSubscriptionsAnalytics) {
          setOverAllSubscription({
            ...subsRes.data.overAllSubscriptionsAnalytics,
            header: 'Overall Subscription Analytics',
          })
        }
      } catch (error) {
        console.error('Error fetching filtered data:', error)
      } finally {
        setIsDataLoading(false)
      }
    }

    fetchFilteredData()
  }, [startDate, endDate, filterMode])

  const [displayStartDate, setDisplayStartDate] = useState<string>('');
  const [displayEndDate, setDisplayEndDate] = useState<string>('');

  useEffect(() => {
    const today = new Date();

    const formatDate = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months are 0-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    setDisplayStartDate(startDate ?? formatDate(firstOfMonth));
    setDisplayEndDate(endDate ?? formatDate(today));
  }, [startDate, endDate]);

  const now = new Date();

// Start of the month (e.g., 2025-07-01T00:00:00.000Z)
const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

// End of the month (e.g., 2025-07-31T23:59:59.999Z)
const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

// Helper to format YYYY-MM-DD
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

  // Memoize expensive calculations to prevent recalculation on every render
  const totalRevenue = useMemo(() => {
    const data = filterMode === 'current_month' ? chartData : overallData
    return (
      safeParseInt(data?.totalAndroidYearlySubscribedUsers) * 99.99 +
      safeParseInt(data?.totalAndroidMonthlySubscribedUsers) * 9.99 +
      safeParseInt(data?.totalIosYearlySubscribedUsers) * 99.99 +
      safeParseInt(data?.totalIosMonthlySubscribedUsers) * 9.99
    ).toFixed(2)
  }, [filterMode, chartData, overallData])

  const androidRevenue = useMemo(() => {
    const data = filterMode === 'current_month' ? chartData : overallData
    return (
      safeParseInt(data?.totalAndroidYearlySubscribedUsers) * 99.99 +
      safeParseInt(data?.totalAndroidMonthlySubscribedUsers) * 9.99
    ).toFixed(2)
  }, [filterMode, chartData, overallData])

  const iosRevenue = useMemo(() => {
    const data = filterMode === 'current_month' ? chartData : overallData
    return (
      safeParseInt(data?.totalIosYearlySubscribedUsers) * 99.99 +
      safeParseInt(data?.totalIosMonthlySubscribedUsers) * 9.99
    ).toFixed(2)
  }, [filterMode, chartData, overallData])

  const totalMonthlyPlanUsers = useMemo(() => {
    const data = filterMode === 'current_month' ? chartData : overallData
    return safeParseInt(data?.totalAndroidMonthlySubscribedUsers) + 
           safeParseInt(data?.totalIosMonthlySubscribedUsers)
  }, [filterMode, chartData, overallData])

  const totalYearlyPlanUsers = useMemo(() => {
    const data = filterMode === 'current_month' ? chartData : overallData
    return safeParseInt(data?.totalAndroidYearlySubscribedUsers) + 
           safeParseInt(data?.totalIosYearlySubscribedUsers)
  }, [filterMode, chartData, overallData])

  // Check if we're loading media/content data
  const isMediaLoading = mediaResponse.loading || albumResponse.loading || !mediaData.videoCount || !albumCount.audioAlbum
  
  // Check if user analytics data is loading
  const isUserDataLoading = monthlyAnalyticsResponse.loading || isDataLoading || 
    (filterMode === 'current_month' && !chartData.total) || 
    (filterMode !== 'current_month' && !overallData.total)

  return (
    <>
      {/* begin::Row */}
      <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isMediaLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${albumCount?.audioAlbum}`}
            description='Total Audio Albums'
            progressNumber=''
            progressBar='ongoing contest'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isMediaLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mx-1 mb-3 mb-xl-5'
            cardNumber={`${mediaData?.audioCount}`}
            description='Total Individual Audios'
            progressNumber=''
            progressBar='ongoing contest'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isMediaLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mx-1 mb-3 mb-xl-5'
            cardNumber={`${albumCount?.videoAlbum}`}
            description='Total Video Albums'
            progressNumber=''
            progressBar='ongoing contest'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isMediaLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${mediaData?.videoCount}`}
            description='Total Individual Videos'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isDataLoading || !prioritiesData.featuredSeriesCount ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${prioritiesData?.featuredSeriesCount}`}
            description='Total Featured Series'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isDataLoading || !prioritiesData.featuredVideosCount ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${prioritiesData?.featuredVideosCount}`}
            description='Total Featured Videos'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isDataLoading || !prioritiesData.popularSeriesCount ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${prioritiesData?.popularSeriesCount}`}
            description='Total Popular Series'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isDataLoading || !prioritiesData.recommendedPlaylistsCount ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${prioritiesData?.recommendedPlaylistsCount}`}
            description='Total Latest Albums'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          {isMediaLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${mediaData?.reelsCount}`}
            description='Total Reels'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        {/* Top Level Filter */}
        <div className='d-flex justify-content-between align-items-center mb-5'>
          <h3 className='mb-0'>Overall Analytics</h3>
          <div className='d-flex gap-3 align-items-center'>
            <div className='btn-group' role='group'>
              <button
                type='button'
                className={`btn btn-sm ${filterMode === 'overall' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setFilterMode('overall')}
              >
                Overall
              </button>
              <button
                type='button'
                className={`btn btn-sm ${filterMode === 'current_month' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setFilterMode('current_month')}
              >
                Current Month
              </button>
              <button
                type='button'
                className={`btn btn-sm ${filterMode === 'custom' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setFilterMode('custom')}
              >
                Calendar Selection
              </button>
            </div>
            {filterMode === 'custom' && (
              <DashboardFilter
                sDate={startDate}
                eDate={endDate}
                setSDate={setStartDate}
                setEDate={setEndDate}
              />
            )}
          </div>
        </div>

        {/* Date Range Display */}
        {filterMode === 'custom' && displayStartDate && displayEndDate && (
          <div className='mb-3'>
            <small className='text-muted'>
              Showing data from {displayStartDate} to {displayEndDate}
            </small>
          </div>
        )}
        {filterMode === 'current_month' && (
          <div className='mb-3'>
            <small className='text-muted'>
              Showing data from {formatDate(startOfMonth)} to {formatDate(endOfMonth)}
            </small>
          </div>
        )}

        {/* User Data Section */}
        <h4 className='mt-5 mb-4'>User Data</h4>
        
        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isUserDataLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.total : overallData?.total}`}
            description='Total Users'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isUserDataLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.subscribedUsersCount : overallData?.subscribedUsersCount}`}
            description='Subscribed'
            progressNumber='437'
            progressBar='total users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isUserDataLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.freeUsersCount : overallData?.freeUsersCount}`}
            description='Free'
            progressNumber='437'
            progressBar='Free users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isUserDataLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.androidUsersCount : overallData?.androidUsersCount}`}
            description='Android Users'
            progressNumber='437'
            progressBar='Android users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          {isUserDataLoading ? (
            <CardSkeleton className='h-100px mb-3 mb-xl-5' />
          ) : (
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.iosUsersCount : overallData?.iosUsersCount}`}
            description='iOS Users'
            progressNumber='437'
            progressBar='Ios users'
              color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
          )}
        </div>

        {/* Subscription Data Section */}
        <h4 className='mt-5 mb-4'>Subscription Data</h4>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.subscribedUsersCount : overallData?.subscribedUsersCount}`}
            description='Total Subscribed Users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${totalMonthlyPlanUsers}`}
            description='Monthly Plan Users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${totalYearlyPlanUsers}`}
            description='Yearly Plan Users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalIosSubscribedUsers : overallData?.totalIosSubscribedUsers}`}
            description='Total iOS Subscribers'
            progressNumber='437'
            progressBar='total ios subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalIosMonthlySubscribedUsers : overallData?.totalIosMonthlySubscribedUsers}`}
            description='Monthly Plan iOS'
            progressNumber='437'
            progressBar='total ios subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalIosYearlySubscribedUsers : overallData?.totalIosYearlySubscribedUsers}`}
            description='Yearly Plan iOS'
            progressNumber='437'
            progressBar='total ios subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAndroidSubscribedUsers : overallData?.totalAndroidSubscribedUsers}`}
            description='Total Android Subscribers'
            progressNumber='437'
            progressBar='total android subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAndroidMonthlySubscribedUsers : overallData?.totalAndroidMonthlySubscribedUsers}`}
            description='Monthly Plan Android'
            progressNumber='437'
            progressBar='total android subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAndroidYearlySubscribedUsers : overallData?.totalAndroidYearlySubscribedUsers}`}
            description='Yearly Plan Android'
            progressNumber='437'
            progressBar='total android subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        {/* Audio Subscription Data Section */}
        <h4 className='mt-5 mb-4'>Audio Subscription Data</h4>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAudioSubscribers : overallData?.totalAudioSubscribers}`}
            description='Total Audio Subscribers'
            progressNumber='437'
            progressBar='total audio subscribed users'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAudioMonthlySubscribers : overallData?.totalAudioMonthlySubscribers}`}
            description='Audio Monthly Plan'
            progressNumber='437'
            progressBar='audio monthly subscribers'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAudioYearlySubscribers : overallData?.totalAudioYearlySubscribers}`}
            description='Audio Yearly Plan'
            progressNumber='437'
            progressBar='audio yearly subscribers'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalIosAudioMonthlySubscribers : overallData?.totalIosAudioMonthlySubscribers}`}
            description='iOS Audio Monthly'
            progressNumber='437'
            progressBar='ios audio monthly'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalIosAudioYearlySubscribers : overallData?.totalIosAudioYearlySubscribers}`}
            description='iOS Audio Yearly'
            progressNumber='437'
            progressBar='ios audio yearly'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAndroidAudioMonthlySubscribers : overallData?.totalAndroidAudioMonthlySubscribers}`}
            description='Android Audio Monthly'
            progressNumber='437'
            progressBar='android audio monthly'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${filterMode === 'current_month' ? chartData?.totalAndroidAudioYearlySubscribers : overallData?.totalAndroidAudioYearlySubscribers}`}
            description='Android Audio Yearly'
            progressNumber='437'
            progressBar='android audio yearly'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        {/* Revenue Data Section */}
        <h4 className='mt-5 mb-4'>Revenue Data</h4>

        <div className='col-sm-6 col-lg-4 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${totalRevenue}`}
            description='Total Subscription Revenue'
            progressNumber='437'
            progressBar='total revenue'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-4 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${androidRevenue}`}
            description='Android Revenue'
            progressNumber='437'
            progressBar='total android revenue'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-4 min-h-100px'>
          <ConditionalCard
            isLoading={isUserDataLoading}
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${iosRevenue}`}
            description='iOS Revenue'
            progressNumber='437'
            progressBar='total ios revenue'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        {/* Commented out - Additional data that might be needed later */}
        {/* <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${overallData?.totalNewSubscribers}`}
            description='Total New Subscribed Users'
            progressNumber='437'
            progressBar='total new subscribed users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>
        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${overallData?.renewedCount}`}
            description='Total Renewed new subscriptions'
            progressNumber='437'
            progressBar='total renewed users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div> */}

        {/* Commented out - Newly registered users analytics section */}
        {/* <h3>Newly registered users analytics   <span style={{ fontWeight: 400 }}>
              {' '}<br />
              <small>
                    (Showing data from {formatDate(startOfMonth)} to {formatDate(endOfMonth)})
              </small>
            </span> </h3>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.total}`}
            description='Total users registered this month'
            progressNumber='437'
            progressBar='Total new users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.freeUsersCount}`}
            description='New free users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.subscribedUsersCount}`}
            description='New subscribed users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalActiveUsers}`}
            description='Total active users this month'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.activeAndroidUsersCount}`}
            description='Total active users Android'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${
              parseInt(platformAnalytics?.totalActiveUsers) -
              parseInt(platformAnalytics?.activeAndroidUsersCount)
            }`}
            description='Total active users iOS'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalAndroidSubscribedUsers}`}
            description='Android subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalAndroidMonthlySubscribedUsers}`}
            description='Android monthly subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalAndroidYearlySubscribedUsers}`}
            description='Android yearly subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${(
              parseInt(chartData?.totalAndroidYearlySubscribedUsers as string) * 99.99 +
              parseInt(chartData?.totalAndroidMonthlySubscribedUsers as string) * 9.99
            ).toFixed(2)}`}
            description='Android subscriptions revenue'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalIosSubscribedUsers}`}
            description='Ios subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalIosMonthlySubscribedUsers}`}
            description='Ios monthly subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>
        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${chartData?.totalIosYearlySubscribedUsers}`}
            description='Ios yearly subscriptions'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${(
              parseInt(chartData?.totalIosYearlySubscribedUsers as string) * 99.99 +
              parseInt(chartData?.totalIosMonthlySubscribedUsers as string) * 9.99
            ).toFixed(2)}`}
            description='Ios subscriptions revenue'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div> */}

        {/* Commented out - Current Month Subscriptions section */}
        {/* <h3>Current Month Subscriptions  <span style={{ fontWeight: 400 }}>
              {' '}<br />
              <small>
                    (Showing data from {formatDate(startOfMonth)} to {formatDate(endOfMonth)})
              </small>
            </span> </h3>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalAndroidCountM}`}
            description='Android Total Subscriptions'
            progressNumber='437'
            progressBar='Total Android Users '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalAndroidMonthlyPlan}`}
            description='Android Monthly plan Subscribers'
            progressNumber='437'
            progressBar='Total iOS Users '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalAndroidYearlyPlan}`}
            description='Android Yearly plan Subscribers'
            progressNumber='437'
            progressBar='Total iOS Users '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${(
              parseInt(platformAnalytics?.totalAndroidMonthlyPlan) * 9.99 +
              parseInt(platformAnalytics?.totalAndroidYearlyPlan) * 99.99
            ).toFixed(2)}`}
            description='Android Total  Revenues'
            progressNumber='437'
            progressBar='Total Monthly  Revenues '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalIosCountM}`}
            description='iOS Total Subscribers'
            progressNumber='437'
            progressBar='Total iOS Users '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalIosMonthlyPlan}`}
            description='iOS Monthly Subscribers'
            progressNumber='437'
            progressBar='Total Monthly  Revenues '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${platformAnalytics?.totalIosYearlyPlan}`}
            description='iOS Yearly Subscribers'
            progressNumber='437'
            progressBar='Total Monthly  Revenues '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-4 col-lg-3 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`$ ${(
              parseInt(platformAnalytics?.totalIosMonthlyPlan) * 9.99 +
              parseInt(platformAnalytics?.totalIosYearlyPlan) * 99.99
            ).toFixed(2)}`}
            description='iOS Total Revenues'
            progressNumber='437'
            progressBar='Total Monthly  Revenues '
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div> */}

        {/* <div className='col-sm-6 col-lg-6 min-h-100px col-sm-6 '>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${monthlySubscription?.subscribedUsers}`}
            description='Monthly Subscribed Users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div>

        <div className='col-sm-6 col-lg-6 min-h-100px col-sm-6'>
          <CardsWidget20
            className='h-100px mb-3 mb-xl-5'
            cardNumber={`${monthlySubscription?.freeUsers}`}
            description='Monthly Free Users'
            progressNumber='437'
            progressBar='total users'
            color='#7239EA'
            img={toAbsoluteUrl('/media/patterns/vector-1.png')}
          />
        </div> */}

        {/* <div className='col-md-6 h-md-50 h-lg-50 col-lg-6 col-xl-6 col-xxl-6 mb-md-3 mb-xl-5'>
          <CardsWidget18 chartData={chartData} className='h-md-50 mb-3 mb-xl-5' />
        </div>

        <div className='col-md-6 h-md-50 h-lg-50 col-lg-6 col-xl-6 col-xxl-6 mb-md-3 mb-xl-5'>
          <CardsWidget18 chartData={overallData} className='h-md-50 mb-3 mb-xl-5' />
        </div>

        <div className='col-md-6 h-md-50 h-lg-50 col-lg-6 col-xl-6 col-xxl-6 mb-md-3 mb-xl-5'>
          <CardsWidget17 chartData={monthlySubscription} className='h-md-50 mb-3 mb-xl-5' />
        </div>

        <div className='col-md-6 h-md-50 h-lg-50 col-lg-6 col-xl-6 col-xxl-6 mb-md-3 mb-xl-5'>
          <CardsWidget17 chartData={overAllSubscription} className='h-md-50 mb-3 mb-xl-5' />
        </div> */}
      </div>

      {/* begin::Col */}
      <div className='col-12 '>
        <EngageWidget10 className='h-md-100' />
      </div>
      {/* end::Col */}

      {/* begin::Media Metrics Dashboard */}
      {/* <div className='col-12 mb-5 mb-xl-10'>
        <MediaMetricsDashboard dateRange="30" />
      </div> */}
      {/* end::Media Metrics Dashboard */}

      {/* begin::Row */}
      {/* <div className='row gy-5 g-xl-8'>
      <div className='col-xl-4'>
        <ListsWidget2 className='card-xl-stretch mb-xl-8' />
      </div>
      <div className='col-xl-4'>
        <ListsWidget6 className='card-xl-stretch mb-xl-8' />
      </div>
      <div className='col-xl-4'>
        <ListsWidget4 className='card-xl-stretch mb-5 mb-xl-8' items={5} />
      </div>
    </div> */}
      {/* partials/widgets/lists/_widget-4', 'class' => 'card-xl-stretch mb-5 mb-xl-8', 'items' => '5' */}
      {/* end::Row */}

      {/* <div className='row g-5 gx-xxl-8'>
      <div className='col-xxl-4'>
        <MixedWidget8
          className='card-xxl-stretch mb-xl-3'
          chartColor='success'
          chartHeight='150px'
        />
      </div>
      <div className='col-xxl-8'>
        <TablesWidget5 className='card-xxl-stretch mb-5 mb-xxl-8' />
      </div>
    </div> */}
    </>
  )
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
